/**
 * AuthKey.io Email API Service
 * Handles direct API calls to AuthKey.io for sending emails
 */

import { FEATURES, AUTHKEY_CONFIG as ENV_CONFIG } from '../config/env';

// AuthKey API Configuration
const AUTHKEY_CONFIG = {
  API_KEY: ENV_CONFIG.API_KEY,
  EMAIL_TEMPLATE_ID: ENV_CONFIG.EMAIL_OTP_TEMPLATE_ID,
  EMAIL_EVENT_ID: ENV_CONFIG.EMAIL_OTP_EVENT_ID,
  API_URL: 'https://api.authkey.io/request',
};

class AuthKeyService {
  /**
   * Send email using AuthKey.io API
   * @param {string} email - Recipient email address
   * @param {number|null} templateId - Email template ID (mid parameter)
   * @param {number|null} eventId - Event ID (eid parameter) - alternative to templateId
   * @param {Object} params - Dynamic template parameters (e.g., {otp: '123456', name: 'John'})
   * @returns {Promise<Object>} Response with success status and message/error
   */
  async sendEmail(email, templateId = null, eventId = null, params = {}) {
    try {
      // Validate configuration
      if (!AUTHKEY_CONFIG.API_KEY) {
        throw new Error('AUTHKEY_API_KEY is not configured. Please set REACT_APP_AUTHKEY_API_KEY in your .env file.');
      }

      // Use provided template/event ID or fallback to config
      const finalTemplateId = templateId || AUTHKEY_CONFIG.EMAIL_TEMPLATE_ID;
      const finalEventId = eventId || AUTHKEY_CONFIG.EMAIL_EVENT_ID;

      if (!finalTemplateId && !finalEventId) {
        throw new Error('Email template ID or Event ID is required. Please set REACT_APP_AUTHKEY_EMAIL_OTP_TEMPLATE_ID or REACT_APP_AUTHKEY_EMAIL_OTP_EVENT_ID in your .env file.');
      }

      // Build query parameters
      const queryParams = new URLSearchParams({
        authkey: AUTHKEY_CONFIG.API_KEY,
        email: email,
      });

      // Add template ID or event ID
      if (finalEventId) {
        queryParams.append('eid', finalEventId);
      } else if (finalTemplateId) {
        queryParams.append('mid', finalTemplateId);
      }

      // Add dynamic template parameters
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });

      // Make API request
      const url = `${AUTHKEY_CONFIG.API_URL}?${queryParams.toString()}`;
      

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      // Check response status
      if (response.ok) {
        try {
          const data = await response.json();
          return {
            success: true,
            message: 'Email sent successfully',
            data: data,
          };
        } catch (jsonError) {
          const text = await response.text();
          return {
            success: true,
            message: 'Email sent successfully',
            data: text,
          };
        }
      } else {
        const errorText = await response.text();
        const errorMessage = `AuthKey API returned status ${response.status}: ${errorText}`;
        
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to send email via AuthKey API';
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Send OTP email using AuthKey.io
   * @param {string} email - Recipient email address
   * @param {string} otp - OTP code to send
   * @param {string|null} name - User name for personalization (optional)
   * @returns {Promise<Object>} Response with success status
   */
  async sendOTPEmail(email, otp, name = null) {
    const params = {
      otp: otp,
    };

    if (name) {
      params.name = name;
    }

    return this.sendEmail(email, null, null, params);
  }
}

export default new AuthKeyService();

